import React, { Dispatch, ReactElement, SetStateAction, useState } from 'react';

import { ServiceLaunch } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import Button from '@amzn/meridian/button';
import Modal from '@amzn/meridian/modal';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Toggle from '@amzn/meridian/toggle';

import { TerritoryFlag } from 'src/components/TerritoryFlag';

interface ITerritoryConfigurator {
    /**
     * The text that will appear in the button and modal header.
     */
    helperText: string;

    /**
     * The list of all possible territories to choose from.
     */
    launchedTerritoryList: ServiceLaunch[];

    features: ITerritoryFeature[];
}

interface ITerritoryFeature {
    /**
     * The name of the territory-level feature configured by this component.
     */
    featureName: string;

    /**
     * The list of territory IDs to exclude.
     */
    filteredTerritoryIdList: Set<number>;

    /**
     * State setter for the territory ID exclusion list.
     */
    setFilteredTerritoryIdList: Dispatch<SetStateAction<Set<number>>>;
}

/**
 * Button-activated modal for managing exclusions from a list of territories.
 * @param props The component props
 */
export const TerritoryConfigurator: React.FC<ITerritoryConfigurator> = (props) => {
    const { helperText, launchedTerritoryList, features } = props;
    const [territoryModalOpen, setTerritoryModalOpen] = useState(false);

    return (
        <>
            <Button size={'small'} type={'tertiary'} onClick={() => setTerritoryModalOpen(true)}>
                {helperText}
            </Button>
            <Modal
                title={helperText}
                open={territoryModalOpen}
                onClose={() => setTerritoryModalOpen(false)}
                scrollContainer={'modal'}
            >
                <Table headerRows={1} spacing={'small'} showStripes={true}>
                    <TableRow>
                        <TableCell>Territory</TableCell>
                        {features.map(({ featureName }) => (
                            <TableCell key={featureName}>{featureName}</TableCell>
                        ))}
                    </TableRow>
                    {launchedTerritoryList.map(({ territoryID, territoryCode }) => (
                        <TableRow key={territoryID}>
                            <TableCell>
                                <TerritoryFlag
                                    territoryCode={territoryCode!}
                                    showTerritoryName={true}
                                    showTerritoryCode={false}
                                />
                            </TableCell>
                            {features.map((feature) => createFeatureCell(feature, territoryID!))}
                        </TableRow>
                    ))}
                </Table>
            </Modal>
        </>
    );
};

function createFeatureCell(props: ITerritoryFeature, territoryId: number): ReactElement {
    const selectTerritory = (id: number) => {
        return (value: boolean) => {
            props.setFilteredTerritoryIdList((list) => {
                const copySet = new Set(list);
                if (value) {
                    copySet.delete(id);
                } else {
                    copySet.add(id);
                }
                return copySet;
            });
        };
    };

    return (
        <TableCell key={props.featureName}>
            <Toggle
                checked={!props.filteredTerritoryIdList.has(territoryId)}
                onChange={selectTerritory(territoryId)}
            ></Toggle>
        </TableCell>
    );
}
